import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DollarSign, Clock, CheckCircle, Users, TrendingUp, Calendar, Download } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AgentCommissions() {
  const [summary, setSummary] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [report, setReport] = useState(null);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    loadCommissions();
  }, []);

  useEffect(() => {
    if (activeTab === "reports") {
      loadReport();
    }
  }, [activeTab, period]);

  const loadCommissions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/agent/commissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Failed to load commissions");

      const data = await response.json();
      setSummary(data.summary);
      setCommissions(data.commissions);
    } catch (error) {
      toast.error("Failed to load commission data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/agent/commissions/report?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Failed to load report");

      const data = await response.json();
      setReport(data.report);
    } catch (error) {
      toast.error("Failed to load report");
      console.error(error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-[#1565C0] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-[#0F172A]">Commission Dashboard</h2>
        <p className="text-sm text-[#475569] mt-1">Track your earnings and commission history</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <DollarSign className="h-8 w-8 text-[#1565C0]" strokeWidth={1.5} />
              <div className="text-right">
                <p className="text-2xl font-bold text-[#0F172A]">{formatCurrency(summary.total_earned)}</p>
                <p className="text-xs text-[#475569] mt-1">Total Earned</p>
              </div>
            </div>
            <div className="text-xs text-[#10B981] flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              {summary.total_customers} customers
            </div>
          </div>

          <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <Clock className="h-8 w-8 text-[#F59E0B]" strokeWidth={1.5} />
              <div className="text-right">
                <p className="text-2xl font-bold text-[#0F172A]">{formatCurrency(summary.pending_amount)}</p>
                <p className="text-xs text-[#475569] mt-1">Pending</p>
              </div>
            </div>
            <p className="text-xs text-[#F59E0B]">Awaiting payout</p>
          </div>

          <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <CheckCircle className="h-8 w-8 text-[#10B981]" strokeWidth={1.5} />
              <div className="text-right">
                <p className="text-2xl font-bold text-[#0F172A]">{formatCurrency(summary.paid_amount)}</p>
                <p className="text-xs text-[#475569] mt-1">Paid</p>
              </div>
            </div>
            <p className="text-xs text-[#10B981]">Successfully received</p>
          </div>

          <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <Users className="h-8 w-8 text-[#8B5CF6]" strokeWidth={1.5} />
              <div className="text-right">
                <p className="text-2xl font-bold text-[#0F172A]">{summary.total_customers}</p>
                <p className="text-xs text-[#475569] mt-1">Total Customers</p>
              </div>
            </div>
            <p className="text-xs text-[#475569]">₹100 per customer</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-6">
          {["overview", "reports"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-[#1565C0] text-[#1565C0]'
                  : 'border-transparent text-[#475569] hover:text-[#0F172A]'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-[#0F172A]">Commission History</h3>
            <p className="text-sm text-[#475569] mt-1">Detailed breakdown of all your commissions</p>
          </div>

          {commissions.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-[#475569]">No commissions yet</p>
              <p className="text-sm text-[#94A3B8] mt-1">Start referring customers to earn commissions!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Earned Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Paid Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {commissions.map((comm) => (
                    <tr key={comm.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-[#0F172A]">
                            {comm.customer?.name || "Customer"}
                          </p>
                          <p className="text-xs text-[#475569]">{comm.customer?.mobile}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-semibold text-[#0F172A]">{formatCurrency(comm.amount)}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          comm.status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : comm.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          {comm.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#475569]">
                        {formatDate(comm.earned_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#475569]">
                        {comm.paid_at ? formatDate(comm.paid_at) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === "reports" && (
        <div className="space-y-4">
          {/* Period Selector */}
          <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#475569]" />
                <span className="text-sm font-medium text-[#0F172A]">Report Period:</span>
              </div>
              <div className="flex gap-2">
                {["week", "month", "all"].map(p => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize ${
                      period === p
                        ? 'bg-[#1565C0] text-white'
                        : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Report Data */}
          {report && Object.keys(report).length > 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-[#0F172A]">Commission Report - {period}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Period</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase tracking-wider">Customers</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase tracking-wider">Earned</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase tracking-wider">Paid</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase tracking-wider">Pending</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {Object.entries(report).map(([key, data]) => (
                      <tr key={key} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#0F172A]">
                          {key}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-[#475569]">
                          {data.count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-[#0F172A]">
                          {formatCurrency(data.earned)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-[#10B981]">
                          {formatCurrency(data.paid)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-[#F59E0B]">
                          {formatCurrency(data.pending)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm py-12">
              <div className="text-center">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-[#475569]">No report data available</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
